import React, { CSSProperties, useMemo } from 'react';
import clsx from 'clsx';
import { themeColours, themeType, type ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import type { KitCard, KitCardItem, KitCardText } from 'fym-shared/src/music24/story/kitCard';
import { kitCardLayout, kitCardSize } from 'fym-shared/src/music24/story/kitCardLayout';
import type { StoryColours } from 'fym-shared/src/music24/story/storyStyles';
import { keyedStoryLines } from 'fym-shared/src/music24/story/storyText';
import { watermarkTone } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import { kitFontName, useKitWebFonts } from '../../themes/kitWebFonts';
import styles from './CardPreview.module.scss';
import { WatermarkLockup } from './WatermarkLockup';

interface CardPreviewProps {
  card: KitCard;
  look: ReleaseKitLook;
  palette: readonly string[];
  artworkUrl: string | undefined;
  /** Free plans: the watermark of the file. */
  watermark?: boolean;
  /** The width of the preview in px; the height follows the file of the card. */
  width: number;
  className?: string;
}

const FULL: CSSProperties = { position: 'absolute', inset: 0 };

const JUSTIFY: Record<KitCardText['align'], CSSProperties['alignItems']> = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};

/** One line of a card text; the SmartLink after "Listen on" is bold (`boldTail`). Pure. */
export function cardPreviewLineParts(
  line: string,
  boldTail: string | undefined
): { plain: string; bold: string | null } {
  if (boldTail === undefined || boldTail === '' || !line.endsWith(boldTail)) {
    return { plain: line, bold: null };
  }
  return { plain: line.slice(0, line.length - boldTail.length), bold: boldTail };
}

const CardItemView: React.FC<{ item: KitCardItem; artworkUrl: string | undefined; colors: StoryColours }> = ({
  item,
  artworkUrl,
  colors,
}) => {
  const box: CSSProperties = {
    position: 'absolute',
    left: item.box.x,
    top: item.box.y,
    width: item.box.w,
    boxSizing: 'border-box',
  };
  if (item.kind === 'cover') {
    return (
      <div style={{ ...box, ...item.outer }} data-testid="card-preview-cover">
        {item.record !== null && <div style={item.record} />}
        {artworkUrl === undefined ? (
          <div className={styles.picture} style={{ background: colors.accent, ...item.picture }} />
        ) : (
          <img className={styles.picture} src={artworkUrl} alt="" style={item.picture} />
        )}
        {item.tape !== null && <div style={item.tape} />}
      </div>
    );
  }
  if (item.kind === 'shape') {
    return <div style={{ ...box, height: item.box.h, ...item.css }} />;
  }
  if (item.kind === 'watermark') {
    // Design w0: the lockup of the file, the last item of the text column.
    return (
      <WatermarkLockup
        testId="card-preview-watermark"
        lockup={item.lockup}
        tone={watermarkTone(colors.ink, false)}
        x={item.box.x}
        y={item.box.y}
      />
    );
  }
  return (
    <div
      data-testid={`card-preview-${item.role}`}
      style={{
        ...box,
        height: item.box.h,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: JUSTIFY[item.align],
        textAlign: item.align,
        whiteSpace: 'nowrap',
        fontFamily: kitFontName(item.fontId),
        fontSize: item.sizePx,
        lineHeight: item.lineHeight,
        ...item.css,
      }}
    >
      {keyedStoryLines(item.lines).map(({ key, line }) => {
        const parts = cardPreviewLineParts(line, item.boldTail);
        return (
          <div key={key}>
            <span style={item.lineCss}>
              {parts.plain}
              {parts.bold !== null && <b style={{ fontWeight: 700 }}>{parts.bold}</b>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * The Studio preview of a kit card: a banner, a YouTube thumbnail or a link preview (designs
 * `asset-redesign/g`, `i`, `m`). It is drawn in the browser with the code of the render: the layout
 * and the look of each part come from fym-shared (`kitCardLayout`), the colours from `themeColours`
 * of the cover, the type from `themeType`. Only the crop of the cover can differ: the director picks it.
 */
const CardPreviewComponent: React.FC<CardPreviewProps> = ({
  card,
  look,
  palette,
  artworkUrl,
  watermark = false,
  width,
  className,
}) => {
  useKitWebFonts();
  const { themeId } = look;
  const type = useMemo(() => themeType(look), [look]);
  const colors = useMemo<StoryColours>(() => {
    const colours = themeColours(themeId, palette);
    return {
      ink: colours.background,
      surface: colours.surface,
      text: colours.text,
      accent: colours.accent,
      onAccent: colours.onAccent,
    };
  }, [palette, themeId]);
  const layout = useMemo(
    () =>
      kitCardLayout({
        card,
        themeId,
        fonts: type.fonts,
        typeStyle: { display: type.display, text: type.text },
        colors,
        watermark,
      }),
    [card, colors, themeId, type, watermark]
  );
  const size = kitCardSize(card);
  const scale = width / size.width;
  return (
    <div
      className={clsx(styles.frame, className)}
      style={{ width, height: size.height * scale }}
      data-testid="card-preview"
      data-card={card.kind === 'banner' ? `banner-${card.site}` : card.kind}
    >
      <div
        className={styles.canvas}
        style={{
          width: size.width,
          height: size.height,
          transform: `scale(${scale})`,
          background: colors.ink,
          color: colors.text,
        }}
      >
        {layout.ground !== null && <div style={{ ...FULL, background: layout.ground }} />}
        <div style={{ ...FULL, overflow: 'hidden' }}>
          {layout.deco.map((deco) => (
            <div key={JSON.stringify(deco)} style={deco} />
          ))}
        </div>
        {layout.items.map((item) => (
          <CardItemView
            key={`${item.kind}-${item.kind === 'cover' || item.kind === 'watermark' ? item.kind : item.role}-${item.box.x}-${item.box.y}`}
            item={item}
            artworkUrl={artworkUrl}
            colors={colors}
          />
        ))}
      </div>
    </div>
  );
};

export const CardPreview = TypedMemo(CardPreviewComponent);
